import React, { Fragment, useState } from "react";
import { RiDeleteBin5Line } from "react-icons/ri";
import PropTypes from "prop-types";
import { GoAlertFill } from "react-icons/go";
import useDeletePendingUpdate from "../lib/useDeletePendingUpdate";
import PendingDeleteCard from "./PendingDeleteCard";
import { usePendingDeletes } from "../lib/usePendingDeletes";
import { ReactComponent as ProIcon } from "../../Asset/promptIconW.svg";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PendingUpdateCard = ({ update }) => {
  const deletePendingUpdateMutation = useDeletePendingUpdate();
  const {
    data: pendingDeleteData,
    error: pendingError,
    isLoading: pendingLoading,
  } = usePendingDeletes();
  const pendingDeletes = pendingDeleteData?.data || [];

  const [isDeleteOverlayVisible, setDeleteOverlayVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const availableFieldValues = Object.keys(update).filter(
    (key) => key !== "id" && key !== "__typename" && key !== "_id"
  );

  const handleSelectItem = (fieldName, fieldValue) => {
    const isSelected = selectedItems.some((item) => {
      if (item.fieldName === fieldName) {
        // For objects, compare their stringified content
        if (typeof item.fieldValue === "object" && typeof fieldValue === "object") {
          return JSON.stringify(item.fieldValue) === JSON.stringify(fieldValue);
        }
        // For non-objects, compare directly
        return item.fieldValue === fieldValue;
      }
      return false;
    });
  
    if (isSelected) {
      setSelectedItems(
        selectedItems.filter((item) => {
          if (item.fieldName === fieldName) {
            if (
              typeof item.fieldValue === "object" &&
              typeof fieldValue === "object"
            ) {
              return JSON.stringify(item.fieldValue) !== JSON.stringify(fieldValue);
            }
            return item.fieldValue !== fieldValue;
          }
          return true;
        })
      );
    } else {
      setSelectedItems([...selectedItems, { fieldName, fieldValue }]);
    }
  };
  

  const handleDelete = () => {
    setIsDeleting(true);

    const dataToDelete = {};

    selectedItems.forEach((item) => {
      if (item.fieldName === "statutoryDocuments") {
        dataToDelete.statutoryDocuments = {
          ...dataToDelete.statutoryDocuments,
          ...item.fieldValue,
        };
      } else if (item.fieldName === "licenses") {
        dataToDelete.licenses = {
          others: [...(dataToDelete.licenses?.others || []), item.fieldValue],
        };
      } else if (item.fieldName === "otherDocuments") {
        dataToDelete.otherDocuments = [
          ...(dataToDelete.otherDocuments || []),
          item.fieldValue,
        ];
      } else {
        dataToDelete[item.fieldName] = item.fieldValue;
      }
    });

    deletePendingUpdateMutation.mutate(dataToDelete, {
      onSuccess: () => {
        setDeleteOverlayVisible(false);
        setSelectedItems([]);
        toast.success("Pending update deleted successfully!", {
          position: toast.POSITION.TOP_RIGHT,
        });
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message ||
            "An error occurred while deleting the document.",
          { position: toast.POSITION.TOP_RIGHT }
        );
      },
      onSettled: () => {
        setIsDeleting(false);
      },
    });
  };

  const handleDeleteCancel = () => {
    setDeleteOverlayVisible(false);
  };

  const handleDeleteOverlayOpen = () => {
    setDeleteOverlayVisible(true);
  };

  const hasSelectedItems = selectedItems.length > 0;

  return (
    <Fragment>
      <ToastContainer />
      {isDeleteOverlayVisible && (
        <div className="delete-per-overlay">
          <div className="card">
            <GoAlertFill className="icon" />
            <h3>Delete</h3>
            <p>Are you sure you want to delete the following selected items?</p>
            <div className="btn">
              <button onClick={handleDeleteCancel}>Cancel</button>
              <button onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {availableFieldValues.length > 0 && (
        <div className="pending-update-card">
          <div className="per-profile-update">
            <span>
              <h3>Personal Profile</h3>
            </span>
            {availableFieldValues.map((key) => {
              const fieldValue = update[key]?.fieldValue;

              if (!fieldValue) return null;

              return (
                <div key={key} className="profile-field">
                  <h4>{key}</h4>
                  <div className="in">
                    {key === "licenses" && Array.isArray(fieldValue?.others) ? (
                      fieldValue.others.map((license, index) => (
                        <div key={index} className="document-row">
                          <p>{license.name}</p>
                          <input
                            type="checkbox"
                            checked={selectedItems.some(
                              (item) =>
                                item.fieldName === "licenses" &&
                                item.fieldValue === license
                            )}
                            onChange={() =>
                              handleSelectItem("licenses", license)
                            }
                          />
                          <a
                            href={license.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </div>
                      ))
                    ) : key === "statutoryDocuments" &&
                      typeof fieldValue === "object" ? (
                      Object.entries(fieldValue).map(([docName, url]) => (
                        <div key={docName} className="document-row">
                          <p>{docName}</p>  
                          <input
                            type="checkbox"
                            checked={selectedItems.some(
                              (item) =>
                                item.fieldName === "statutoryDocuments" &&
                              item.fieldValue[docName]
                            )}
                            onChange={() =>
                              handleSelectItem("statutoryDocuments", {
                                [docName]: url,
                              })
                            }
                            />
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            >
                            View
                          </a>
                        </div>
                      ))
                    ) : key === "otherDocuments" &&
                      Array.isArray(fieldValue) ? (
                      fieldValue.map((doc, index) => (
                        <div key={index} className="document-row">
                          <p>{doc.name}</p>
                          <input
                            type="checkbox"
                            checked={selectedItems.some(
                              (item) =>
                                item.fieldName === "otherDocuments" &&
                                item.fieldValue === doc
                            )}
                            onChange={() =>
                              handleSelectItem("otherDocuments", doc)
                            }
                          />
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </div>
                      ))
                    ) : (
                      // Checkbox for fields like email, address, LinkedIn, website
                      <div className="document-row">
                        <p>{fieldValue}</p>
                        <input
                          type="checkbox"
                          checked={selectedItems.some(
                            (item) =>
                              item.fieldName === key &&
                              item.fieldValue === fieldValue
                          )}
                          onChange={() => handleSelectItem(key, fieldValue)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!availableFieldValues.length && !pendingDeletes.length && (
        <div className="no-pending-deletion">
          <ProIcon />
          <p>You currently have no pending documents for approval</p>
        </div>
      )}

      <div className="delete-pending">
        {pendingLoading ? (
          <p>Loading...</p>
        ) : pendingError ? (
          <p>Error: {pendingError.message}</p>
        ) : (
          <PendingDeleteCard
            deleteData={pendingDeletes}
            isLoading={pendingLoading}
            error={pendingError}
          />
        )}
      </div>

      {hasSelectedItems && (
        <button
          onClick={handleDeleteOverlayOpen}
          className="delete-pending-btn"
        >
          <RiDeleteBin5Line /> Delete Selected
        </button>
      )}
    </Fragment>
  );
};

PendingUpdateCard.propTypes = {
  update: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.object,
};

export default PendingUpdateCard;
